/**
 * CounterpartCards — presentational atoms for the role-scoped War Room stage.
 *
 * Design: K3 spec (docs scratch: k3_design_verdict.md). THREE-HUE BUDGET —
 * `var(--color-brand)` (#FF6600) means *yours* (hero left rail only),
 * `var(--color-blocker)` red means *against you* (adversary rail + blockers +
 * negative money), `var(--color-verified)` green means *verified*. Everything
 * else is grayscale hierarchy. No new color tokens.
 *
 * Pure presentation — no fetching. Data comes from {@link SeatCard} (see
 * services/deliberationApi.extractSeatCards). Per K3 §4: NEVER fabricate a
 * verification chip when the payload lacks the field — absence over invention.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, ShieldAlert, ArrowDown } from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';
import type { SeatCard } from '@/services/deliberationApi';

const card: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 2,
};

/** Parse a Decimal-string seat value to a number for FinancialValue. */
function numOrNull(v: string | null): number | null {
  if (v == null || v === '') return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

// ── SectionHeader ────────────────────────────────────────────────────────────
export function SectionHeader({ title, micro }: { title: string; micro?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 mb-3">
      <h2 className="cs-display" style={{ fontSize: 20, lineHeight: 1.1 }}>{title}</h2>
      {micro && (
        <span className="text-[11px] font-mono tabular-nums truncate" style={{ color: 'var(--color-text-dim)' }}>
          {micro}
        </span>
      )}
    </div>
  );
}

/** One-line seat mandate — what this AI seat is FOR (K3 §5 row 2). */
const SEAT_MANDATE: Record<string, string> = {
  'ai:forensic': 'Calculates the defensible loss',
  'ai:attorney': 'Argues the recoverable case',
  'ai:adjuster': "Holds the carrier's position",
  'ai:defendant_attorney': 'Simulates opposing counsel',
  'ai:document_auditor': 'Audits document health',
  'ai:data_specialist': 'Grades data readiness',
};

// ── State chip (K3 §6 — only render a real signal; no fabricated VERIFIED). In the
// hero the metric lives in the metrics row, so the chip must NOT repeat it
// (showMetric=false); in the compact full-room card the chip is the only metric.
export function StateChip({ seat, showMetric = true }: { seat: SeatCard; showMetric?: boolean }) {
  if (seat.blocked) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-mono" style={{ color: 'var(--color-blocker)' }}>
        <ShieldAlert className="w-3.5 h-3.5" /> BLOCKER
      </span>
    );
  }
  if (showMetric && seat.score != null) {
    return (
      <span className="text-[11px] font-mono tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
        {seat.metricLabel ? `${seat.metricLabel.toUpperCase()} ` : ''}{seat.score}
      </span>
    );
  }
  return null;
}

interface HeroProps {
  /** null + pending renders the "analysis in progress" placeholder (K3 running state). */
  seat: SeatCard | null;
  /** The seat name to show in the placeholder when `seat` is null. */
  placeholderName?: string;
  /** running/partial → dashed placeholder geometry. */
  pending?: boolean;
  /** attorney duplication note: hero metrics are pre-synthesis. */
  preSynthesis?: boolean;
  runId?: string | null;
  completedAt?: string | null;
}

// ── CounterpartHeroCard — "this is YOUR AI" (K3 §5) ──────────────────────────
export function CounterpartHeroCard({ seat, placeholderName, pending, preSynthesis, runId, completedAt }: HeroProps) {
  if (!seat) {
    return (
      <div
        className="p-6"
        style={{
          backgroundColor: 'var(--color-bg-elevated)',
          border: '1px dashed var(--color-border)',
          borderLeft: '3px solid var(--color-brand)',
          borderRadius: 2,
          opacity: pending ? 1 : 0.7,
        }}
        data-testid="counterpart-hero-pending"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em]" style={{ color: 'var(--color-brand)' }}>
          Your AI counterpart
        </p>
        <p className="cs-display mt-1" style={{ fontSize: 24, color: 'var(--color-text-muted)' }}>
          {placeholderName || 'Awaiting seat'}
        </p>
        <p className="text-xs mt-2" style={{ color: 'var(--color-text-dim)' }}>Analysis in progress</p>
      </div>
    );
  }

  const value = numOrNull(seat.value);
  return (
    <div
      className="p-6"
      style={{ ...card, backgroundColor: 'var(--color-bg-elevated)', borderLeft: '3px solid var(--color-brand)' }}
      data-testid="counterpart-hero"
    >
      {/* Row 1 — identity */}
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em]" style={{ color: 'var(--color-brand)' }}>
          Your AI counterpart
        </p>
        <StateChip seat={seat} showMetric={false} />
      </div>
      {/* Row 2 — name + one-line seat mandate */}
      <h3 className="cs-display mt-1" style={{ fontSize: 28, lineHeight: 1.05, color: 'var(--color-text-primary)' }}>
        {seat.displayName}
      </h3>
      <p className="text-xs font-mono mt-0.5" style={{ color: 'var(--color-text-dim)' }}>
        {seat.seat}{SEAT_MANDATE[seat.seat] ? ` · ${SEAT_MANDATE[seat.seat]}` : ''}
      </p>
      {/* Row 3 — metrics */}
      <div className="flex flex-wrap gap-x-8 gap-y-2 mt-4 pt-4" style={{ borderTop: '1px solid var(--color-border-sub)' }}>
        <div>
          <p className="text-[11px] uppercase" style={{ color: 'var(--color-text-dim)' }}>{seat.metricLabel}</p>
          <p className="text-[15px] font-mono tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
            {seat.score != null ? seat.score : '—'}
          </p>
        </div>
        {value != null && (
          <div>
            <p className="text-[11px] uppercase" style={{ color: 'var(--color-text-dim)' }}>Seat figure</p>
            <p className="text-[15px] font-mono tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
              <FinancialValue value={value} />
            </p>
          </div>
        )}
      </div>
      {/* Row 4 — narrative */}
      {seat.reasoning && (
        <p className="mt-4 text-sm" style={{ color: 'var(--color-text-secondary)', lineHeight: 1.55, maxWidth: '68ch' }}>
          {seat.reasoning}
        </p>
      )}
      {/* Row 5 — findings (blockers already flagged in the chip) */}
      {seat.findings.length > 0 && (
        <ul className="mt-4 divide-y" style={{ borderColor: 'var(--color-border-faint)' }}>
          {seat.findings.map((f, i) => (
            <li key={i} className="py-2 text-sm flex gap-2" style={{ color: 'var(--color-text-secondary)' }}>
              <span aria-hidden="true" style={{ color: 'var(--color-text-dim)' }}>–</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}
      {/* Row 6 — provenance */}
      {(runId || completedAt || preSynthesis) && (
        <p className="mt-4 text-[11px] font-mono" style={{ color: 'var(--color-text-dim)' }}>
          {runId ? `from deliberation ${runId}` : ''}{completedAt ? ` · ${completedAt}` : ''}
          {preSynthesis ? ' · Seat analysis — pre-synthesis' : ''}
        </p>
      )}
    </div>
  );
}

// ── AdversaryBriefCard — defense attack preview (K3 §1C, red rail) ───────────
export function AdversaryBriefCard({
  seat,
  counterStrategy,
}: {
  seat: SeatCard | null;
  counterStrategy?: Array<{ defense_argument: string; our_counter: string | null }>;
}) {
  if (!seat) return null;
  return (
    <div
      className="p-5"
      style={{ ...card, backgroundColor: 'var(--color-bg-elevated)', borderLeft: '3px solid var(--color-blocker)' }}
      data-testid="counterpart-adversary"
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em]" style={{ color: 'var(--color-blocker)' }}>
        Defense attack preview — simulated opposing counsel
      </p>
      <h3 className="cs-display mt-1" style={{ fontSize: 22, lineHeight: 1.05, color: 'var(--color-text-primary)' }}>
        {seat.displayName}
      </h3>
      {seat.reasoning && (
        <p className="mt-3 text-sm" style={{ color: 'var(--color-text-secondary)', lineHeight: 1.55, maxWidth: '68ch' }}>
          {seat.reasoning}
        </p>
      )}
      {seat.findings.length > 0 && (
        <ul className="mt-3 divide-y" style={{ borderColor: 'var(--color-border-faint)' }}>
          {seat.findings.map((f, i) => (
            <li key={i} className="py-2 text-sm flex gap-2" style={{ color: 'var(--color-text-secondary)' }}>
              <span aria-hidden="true" style={{ color: 'var(--color-text-dim)' }}>–</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}
      {counterStrategy && counterStrategy.length > 0 && (
        <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--color-border-sub)' }}>
          <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
            Room counter-strategy
          </p>
          <ol className="mt-2 space-y-2">
            {counterStrategy.map((c, i) => (
              <li key={i} className="text-sm">
                <span style={{ color: 'var(--color-text-secondary)' }}>{i + 1}. {c.defense_argument}</span>
                {c.our_counter && (
                  <span className="block text-xs mt-0.5 pl-4" style={{ color: 'var(--color-text-muted)' }}>
                    → {c.our_counter}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// ── FullRoomDisclosure — collapsed by default (K3 §1E) ───────────────────────
export function FullRoomDisclosure({
  seatCards,
  heroSeats,
  order,
}: {
  seatCards: Record<string, SeatCard>;
  heroSeats: string[];
  order?: string[];
}) {
  const [open, setOpen] = useState(false);
  const names = (order && order.length ? order.filter((n) => seatCards[n]) : Object.keys(seatCards));
  // Include any seats not covered by `order`.
  for (const n of Object.keys(seatCards)) if (!names.includes(n)) names.push(n);
  if (names.length === 0) return null;

  return (
    <div style={card} className="p-4" data-testid="counterpart-fullroom">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Full deliberation
        </span>
        <span className="text-[11px] font-mono tabular-nums" style={{ color: 'var(--color-text-dim)' }}>
          {names.length} seats
        </span>
      </button>
      {open && (
        <div className="mt-3 grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {names.map((n) => {
            const s = seatCards[n];
            const yours = heroSeats.includes(n);
            return (
              <div
                key={n}
                className="p-3"
                style={{
                  backgroundColor: 'var(--color-bg-base)',
                  border: '1px solid var(--color-border-sub)',
                  borderLeft: yours ? '2px solid var(--color-brand)' : '1px solid var(--color-border-sub)',
                  borderRadius: 2,
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>{s.displayName}</span>
                  {yours ? (
                    <span className="text-[10px] uppercase tracking-wide inline-flex items-center gap-1" style={{ color: 'var(--color-brand)' }}>
                      <ArrowDown className="w-3 h-3" style={{ transform: 'rotate(180deg)' }} /> your seat
                    </span>
                  ) : (
                    <StateChip seat={s} />
                  )}
                </div>
                {s.findings.slice(0, 3).map((f, i) => (
                  <p key={i} className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>– {f}</p>
                ))}
                {s.findings.length > 3 && (
                  <p className="text-[11px] mt-1" style={{ color: 'var(--color-text-dim)' }}>+{s.findings.length - 3} more</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
